import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Helper function to validate URLs
const isValidUrl = (url) => {
  try {
    new URL(url);
    return url.startsWith("http://") || url.startsWith("https://");
  } catch {
    return false;
  }
};

// Define specifications for each sub-form, excluding uploadPhoto and zip_url
const subFormSpecifications = {
  AdhesiveSealant: [
    "expiryMonitoring",
    "lotNo",
    "make",
    "manufacturingDate",
    "model",
    "remarks",
  ],
  Cell: [
    "efficiency",
    "expiryMonitoring",
    "lotNo",
    "make",
    "manufacturingDate",
    "remarks",
    "wattPeak",
  ],
  Frame: ["lotNo", "make", "size", "type"],
  cellFixingType: ["lotNo", "make", "size", "type"],
  evaBack: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model", "size"],
  evaFront: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model", "size"],
  flux: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model"],
  glassBack: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model", "size"],
  glassFront: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model", "size"],
  interconnector: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "size"],
  junctionBox: ["cableCrossSectionLength", "diodeNo", "ipRating", "jbLipHeight", "lotNo", "make", "model"],
  pottingMaterial: ["expiryMonitoringA", "expiryMonitoringB", "lotNo", "make", "manufacturingDateA", "manufacturingDateB", "model"],
  stringConnector: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "size"],
};

// Human-readable labels for specifications
const specificationLabels = {
  expiryMonitoring: "Expiry Monitoring",
  expiryMonitoringA: "Expiry Monitoring A",
  expiryMonitoringB: "Expiry Monitoring B",
  lotNo: "Lot No",
  make: "Make",
  manufacturingDate: "Manufacturing Date",
  manufacturingDateA: "Manufacturing Date A",
  manufacturingDateB: "Manufacturing Date B",
  model: "Model",
  remarks: "Remarks",
  efficiency: "Efficiency",
  wattPeak: "Watt Peak",
  size: "Size",
  type: "Type",
  cableCrossSectionLength: "Cable Cross Section Length",
  diodeNo: "Diode No",
  ipRating: "IP Rating",
  jbLipHeight: "JB Lip Height",
  zip_url: "Zip URL",
};

// Human-readable labels for sub-forms
const subFormLabels = {
  AdhesiveSealant: "Adhesive Sealant (BOM)",
  Cell: "Cell (BOM)",
  Frame: "Frame (BOM)",
  cellFixingType: "Cell Fixing Type (BOM)",
  evaBack: "EVA Back (BOM)",
  evaFront: "EVA Front (BOM)",
  flux: "Flux (BOM)",
  glassBack: "Glass Back (BOM)",
  glassFront: "Glass Front (BOM)",
  interconnector: "Interconnector (BOM)",
  junctionBox: "Junction Box (BOM)",
  pottingMaterial: "Potting Material (BOM)",
  stringConnector: "String Connector (BOM)",
};

const BOMTable = ({ data = [], startDate, endDate, shifts = [], subForm = [] }) => {
  // Define static table headers
  const staticHeaders = [
    { id: "subForm", label: "Sub-form" },
    { id: "specification", label: "Specification" },
  ];

  // Get all dates in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to data, or default
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["Morning", "Afternoon", "Night"];
  }, [shifts, data]);

  // Dynamic flat columns: strictly uniform across all dates and shifts
  const dynamicHeaders = useMemo(() => {
    return dates.flatMap((date) =>
      effectiveShifts.map((shift) => ({
        id: `${date}_${shift}`,
        date,
        shift,
      }))
    );
  }, [dates, effectiveShifts]);

  // Calculate column widths based on content
  const columnWidths = useMemo(() => {
    const widths = {
      subForm: estimateTextWidth("Sub-form"),
      specification: estimateTextWidth("Specification"),
      value: 120,
    };

    Object.entries(subFormSpecifications).forEach(([subFormKey, specs]) => {
      if (subForm.length === 0 || subForm.includes(subFormLabels[subFormKey])) {
        const subFormLabel = subFormLabels[subFormKey] || subFormKey;
        widths.subForm = Math.min(
          Math.max(widths.subForm, estimateTextWidth(subFormLabel)),
          (8 * window.innerWidth) / 100
        );
        specs.forEach((spec) => {
          const specLabel = specificationLabels[spec] || spec;
          widths.specification = Math.min(
            Math.max(widths.specification, estimateTextWidth(specLabel)),
            (8 * window.innerWidth) / 100
          );
        });
        widths.specification = Math.min(
          Math.max(widths.specification, estimateTextWidth(specificationLabels.zip_url)),
          (8 * window.innerWidth) / 100
        );
      }
    });

    return widths;
  }, [subForm]);

  // Calculate cumulative left positions for sticky columns
  const getLeftPosition = (index) => {
    if (index === 0) return 0;
    return columnWidths.subForm;
  };

  // Prepare table rows
  const tableRows = useMemo(() => {
    const rows = [];
    Object.entries(subFormSpecifications).forEach(([subFormKey, specs]) => {
      if (subForm.length === 0 || subForm.includes(subFormLabels[subFormKey])) {
        const subFormLabel = subFormLabels[subFormKey] || subFormKey;

        // Regular specifications
        specs.forEach((spec) => {
          const row = {
            subForm: subFormLabel,
            specification: specificationLabels[spec] || spec,
            specKey: spec,
            data: {},
          };

          dynamicHeaders.forEach((header) => {
            row.data[header.id] = "N/A";
          });

          data.forEach((item) => {
            if (item.form_data?.[subFormKey]) {
              const key = `${formatDate(item.inspectionDate)}_${item.inspectionShift}`;
              if (row.data[key] !== undefined) {
                const val = item.form_data[subFormKey][spec];
                row.data[key] = val !== undefined && val !== null && val !== "" ? String(val) : "N/A";
              }
            }
          });

          rows.push(row);
        });

        // Zip URL row
        const zipRow = {
          subForm: subFormLabel,
          specification: specificationLabels.zip_url,
          specKey: "zip_url",
          data: {},
        };

        dynamicHeaders.forEach((header) => {
          zipRow.data[header.id] = "N/A";
        });

        data.forEach((item) => {
          if (item.sub_form === subFormKey || item.form_data?.[subFormKey]) {
            const key = `${formatDate(item.inspectionDate)}_${item.inspectionShift}`;
            if (zipRow.data[key] !== undefined) {
              const rawUrl = item.zip_url;
              zipRow.data[key] =
                rawUrl && rawUrl.trim() !== "" && isValidUrl(rawUrl)
                  ? rawUrl.trim()
                  : "N/A";
            }
          }
        });

        rows.push(zipRow);
      }
    });
    return rows;
  }, [data, subForm, dynamicHeaders]);

  const shiftsPerDate = effectiveShifts.length;

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    const row1 = ["Sub-form", "Specification"];
    const row2 = ["", ""];

    merges.push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } });
    merges.push({ s: { r: 0, c: 1 }, e: { r: 1, c: 1 } });

    let colIdx = 2;

    dates.forEach((date) => {
      row1.push(date);
      for (let i = 1; i < shiftsPerDate; i++) row1.push("");
      if (shiftsPerDate > 1) {
        merges.push({
          s: { r: 0, c: colIdx },
          e: { r: 0, c: colIdx + shiftsPerDate - 1 },
        });
      }

      effectiveShifts.forEach((shift) => {
        row2.push(`Shift ${shift}`);
        colIdx += 1;
      });
    });

    const dataRows = tableRows.map((row) => {
      const r = [row.subForm, row.specification];
      dynamicHeaders.forEach((header) => {
        const val = row.data[header.id];
        if (row.specKey === "zip_url" && val && val !== "N/A" && val.startsWith("http")) {
          r.push("LINK");
        } else {
          r.push(val ?? "N/A");
        }
      });
      return r;
    });

    const sheetData = [row1, row2, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    tableRows.forEach((row, rowIndex) => {
      if (row.specKey === "zip_url") {
        dynamicHeaders.forEach((header, headerIndex) => {
          const rawUrl = row.data[header.id];
          if (
            rawUrl &&
            rawUrl !== "N/A" &&
            typeof rawUrl === "string" &&
            rawUrl.trim().toLowerCase().startsWith("http")
          ) {
            const cleanUrl = rawUrl.trim();
            const cellAddress = XLSX.utils.encode_cell({
              r: rowIndex + 2,
              c: headerIndex + 2,
            });

            ws[cellAddress] = {
              t: "s",
              v: "LINK",
              l: { Target: cleanUrl, Tooltip: cleanUrl },
            };
          }
        });
      }
    });

    if (merges.length > 0) ws["!merges"] = merges;

    ws["!cols"] = [
      { wch: 22 },
      { wch: 28 },
      ...Array(dynamicHeaders.length).fill({ wch: 18 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "BOM Verification");

    const fileName = generateExcelFileName("BOM_Verification", startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array", bookSST: false });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Bill of Material Verification
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dates.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Sub-form, Specification + Dates (merged across shifts) */}
            <TableRow>
              <StickyTableCell
                rowSpan={2}
                columnWidth={columnWidths.subForm}
                left={getLeftPosition(0)}
                isLastSticky={false}
              >
                Sub-form
              </StickyTableCell>
              <StickyTableCell
                rowSpan={2}
                columnWidth={columnWidths.specification}
                left={getLeftPosition(1)}
                isLastSticky={true}
              >
                Specification
              </StickyTableCell>
              {dates.map((date) => (
                <CustomTableCell
                  key={date}
                  colSpan={shiftsPerDate}
                  columnWidth={columnWidths.value * shiftsPerDate}
                >
                  {date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Shifts under each Date */}
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.map((shift) => (
                  <CustomTableCell
                    key={`${date}_${shift}`}
                    columnWidth={columnWidths.value}
                  >
                    Shift {shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={2}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.subForm + columnWidths.specification}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No dates selected
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={2 + dynamicHeaders.length}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.subForm + columnWidths.specification}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={`${row.subForm}_${row.specification}_${index}`} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.subForm}
                    left={getLeftPosition(0)}
                    isLastSticky={false}
                  >
                    {row.subForm}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.specification}
                    left={getLeftPosition(1)}
                    isLastSticky={true}
                  >
                    {row.specification}
                  </StickyBodyTableCell>

                  {dynamicHeaders.map((header) => {
                    const value = row.data[header.id];
                    return (
                      <BodyTableCell key={`${header.id}_${index}`} columnWidth={columnWidths.value}>
                        {row.specKey === "zip_url" && value !== "N/A" && value.startsWith("http") ? (
                          <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1976d2", textDecoration: "underline" }}
                          >
                            LINK
                          </a>
                        ) : (
                          value ?? "N/A"
                        )}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default BOMTable;